import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  IconFileImport,
  IconInfoCircle,
  IconPencilPlus,
  IconUsersGroup,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { useLeagueFacilityId } from "../../../hooks/useLeagueFacilityId";
import DataTable from "../../common/DataTable";
import { RefreshOverlay } from "../../common/RefreshOverlay";
import { InlineHint } from "../../common/InlineHint";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { AddPlayerDialog } from "../AddPlayerDialog";
import { AddTeamDialog } from "../AddTeamDialog";
import { ImportRosterDialog } from "../ImportRosterDialog";
import { EditRosterMemberDialog } from "../EditRosterMemberDialog";
import {
  getRosterColumns,
  ROSTER_HEADER_CLASS,
  ROSTER_ROW_CLASS,
} from "../../common/rosterColumns";
import { LeagueSubstitutesRail } from "../../LeagueSubstitutesRail";
import { LeagueRosterEmptyState } from "../../empty/LeagueRosterEmptyState";
import {
  useGetLeagueRosterQuery,
  useDeleteRosterMemberMutation,
} from "../../../api/endpoints/roster";
import type { Clubhouse_LeagueV2RosterMemberDto } from "../../../types/leagueRoster";
import {
  Clubhouse_LeagueV2Status,
  type Clubhouse_LeagueScoring,
  type Clubhouse_LeagueType,
  type Clubhouse_LeagueWizardScheduleMode,
  type RosterTeeType,
} from "../../../enums";
import {
  describeFullTimeAddBlock,
  describeOpenRoundCompletionNotice,
  canAddLoneFullTimePlayer,
  requiresCompleteTeamAdd,
  fullTimeRosterRemoval,
  isRoundOneClosed,
  isTeamFormat,
  WITHDRAW_PLAYER_CONFIRMATION,
  MATCH_PLAY_LOCK_MESSAGE,
  SINGLE_PLAYER_TEAM_WITHDRAWAL_MESSAGE,
} from "../../../rosterChangePolicy";
import type { LateEntryRound } from "../../../rosterChangePolicy";
import { useGetLeagueTeamsQuery } from "../../../api/endpoints/teams";
import { isScrambleFormat } from "../advancedLeagueSchema";

/** Small counts read as words on screen; anything larger stays a numeral. */
function spellTeamSize(size: number | null | undefined): string {
  return { 2: "two", 3: "three", 4: "four" }[size ?? 2] ?? String(size);
}

export function RosterTab({
  leagueId,
  leagueMensTee,
  leagueWomensTee,
  status,
  leagueType,
  scoring,
  scheduleMode,
  matchPlayBegun,
  teamSize,
  rounds,
}: {
  leagueId: string;
  /** Men's league tee from Season Setup. Pre-fills men added from this tab. */
  leagueMensTee?: RosterTeeType | null;
  /** Women's league tee from Season Setup. Pre-fills women added from this tab. */
  leagueWomensTee?: RosterTeeType | null;
  /**
   * League format and lifecycle. Omitted while the wizard is still drafting,
   * which reads as "not finalized" and leaves every path open.
   */
  status?: Clubhouse_LeagueV2Status | null;
  leagueType?: Clubhouse_LeagueType | null;
  scoring?: Clubhouse_LeagueScoring | null;
  scheduleMode?: Clubhouse_LeagueWizardScheduleMode | null;
  matchPlayBegun?: boolean | null;
  /** Full-time players per team, for the Add Team form's slot count. */
  teamSize?: number | null;
  /** Saved rounds, so a Weekly late add can name the round that is open now. */
  rounds?: readonly LateEntryRound[] | null;
}) {
  // Roster is scoped to the league's OWN facility, not the sidebar-selected
  // one — the dashboard spans facilities, so the current facility can differ
  // from where the league lives (a mismatch 404s the roster call).
  const facilityId = useLeagueFacilityId(leagueId);

  // Why a full-time add is unavailable, and what a late entrant will actually
  // play. Both come from the same rules the API enforces, so the operator is
  // never offered a button that is certain to fail.
  const policyContext = {
    status,
    type: leagueType,
    scoring,
    scheduleMode,
    matchPlayBegun,
  };
  const addBlockedReason = describeFullTimeAddBlock(policyContext);
  // No round text on this tab — the notice travels with the add itself, into
  // the dialogs below, where it is about the thing being done.
  const openRoundNotice = describeOpenRoundCompletionNotice(
    policyContext,
    rounds,
  );
  // A finalized team league does not add players one at a time — it adds a whole
  // team. That is a different button, not a disabled one, so the operator has
  // somewhere to go.
  const addsByCompleteTeam = requiresCompleteTeamAdd(policyContext);
  // Substitutes are not roster slots, so they stay available in both locked
  // states. A started Match league's own message tells the operator to cover a
  // player with a substitute, so this is the button that has to still work.
  const loneFullTimeAddBlocked = !canAddLoneFullTimePlayer(policyContext);
  const rosterRemoval = fullTimeRosterRemoval(policyContext);
  const isTeamLeague = isTeamFormat(policyContext);
  const hideHandicapColumn = isScrambleFormat(leagueType);
  const canEditRoster = status !== Clubhouse_LeagueV2Status.ARCHIVED;
  // Import only ever writes full-time players, so it follows the full-time
  // rule exactly: available while a lone add is.
  const canImportRoster = canEditRoster && !addBlockedReason;
  // After Round 1 closes nobody new joins, so the full-time add paths are
  // hidden rather than disabled. Substitutes keep their own button below.
  const fullTimeAddsClosed = isRoundOneClosed(rounds);

  const {
    data: rosterData,
    isLoading,
    isFetching,
  } = useGetLeagueRosterQuery(
    { facilityId: facilityId ?? "", leagueId },
    { skip: !facilityId },
  );
  const members = useMemo(
    () => (rosterData ?? []).filter((m) => m.isActive ?? true),
    [rosterData],
  );
  // Only active members block re-adding; an inactive (soft-removed) player
  // should be addable again, so they're left out of the "already in league" set.
  const existingUserIds = members.flatMap((m) =>
    m.userId != null ? [m.userId] : [],
  );

  // The table describes who is playing; the rail beside it describes who is
  // available to cover. Sorted here rather than through the table's own sort
  // state: that state tints the column it sorted by, which reads as "you
  // sorted this" for an order the operator never chose.
  const rosterPlayers = useMemo(
    () =>
      members
        .filter((m) => !m.isSubstituteOnly)
        .sort((a, b) => (a.playerName ?? "").localeCompare(b.playerName ?? "")),
    [members],
  );
  const rosterSubstitutes = useMemo(
    () => members.filter((m) => m.isSubstituteOnly),
    [members],
  );

  // Names the Team column, and suggests the next team's name in Add Team.
  // Teams get their own step in this editor, so there is no Players/Teams
  // switch here — only the column that says which team a player is on.
  const { data: teamsData } = useGetLeagueTeamsQuery(
    { facilityId: facilityId ?? "", leagueId },
    { skip: !facilityId || (!addsByCompleteTeam && !isTeamLeague) },
  );
  const suggestedTeamName = `Team ${(teamsData?.teams?.length ?? 0) + 1}`;
  // The roster row carries only `teamId`; the name is joined from the teams
  // endpoint. Ids are slugs on both sides, so they are keyed as strings.
  const teamNameById = useMemo(() => {
    if (!isTeamLeague) return undefined;
    return new Map(
      (teamsData?.teams ?? []).flatMap((team) =>
        team.teamId == null ? [] : [[String(team.teamId), team.teamName ?? ""]],
      ),
    );
  }, [isTeamLeague, teamsData]);

  const [addOpen, setAddOpen] = useState(false);
  const [addSubstituteOpen, setAddSubstituteOpen] = useState(false);
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editMember, setEditMember] =
    useState<Clubhouse_LeagueV2RosterMemberDto | null>(null);
  const [removeMember, setRemoveMember] =
    useState<Clubhouse_LeagueV2RosterMemberDto | null>(null);
  const [deleteRosterMember, { isLoading: removing }] =
    useDeleteRosterMemberMutation();

  const columns = useMemo(
    () =>
      getRosterColumns({
        onEdit: setEditMember,
        onRemove: setRemoveMember,
        canEdit: canEditRoster,
        // No trash on a team league's player rows: a whole team is withdrawn
        // from the Teams step, and a player who leaves is covered by a sub.
        removal: isTeamLeague ? "none" : rosterRemoval,
        teamNameById,
        hideHandicapColumn,
      }),
    [canEditRoster, hideHandicapColumn, isTeamLeague, rosterRemoval, teamNameById],
  );

  const handleRemove = async () => {
    if (removeMember?.memberId == null) return;
    try {
      await deleteRosterMember({
        facilityId: facilityId ?? "",
        leagueId,
        memberId: removeMember.memberId,
      }).unwrap();
      toast.success(
        rosterRemoval === "withdraw" ? "Player withdrawn" : "Player removed",
        {
          position: "top-center",
          className: "text-sm poppins",
        },
      );
      setRemoveMember(null);
    } catch (err) {
      // Keep the dialog open so the operator can retry; tell them it failed.
      console.error("deleteRosterMember failed", err);
      toast.error(
        rosterRemoval === "withdraw"
          ? "The player could not be withdrawn. Please try again."
          : "The player could not be removed. Please try again.",
        {
          position: "top-center",
          className: "text-sm poppins",
        },
      );
    }
  };

  return (
    // The table, the substitutes panel and the top bar are one block as wide
    // as its content, so the top bar's buttons land on the panel's right edge
    // rather than the step's.
    <div className="flex w-full flex-col gap-3 lg:w-fit">
      {/* Toolbar: no fork between the two entry paths — either builds into the
          same list below. Both open modals so the roster stays put underneath. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="ml-auto flex items-center gap-2">
          {!fullTimeAddsClosed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canImportRoster}
              title={
                canImportRoster ? undefined : (addBlockedReason ?? undefined)
              }
              onClick={() => setImportOpen(true)}
              className="gap-1.5 mulish text-xs rounded-lg"
            >
              <IconFileImport data-icon="inline-start" />
              Import Roster
            </Button>
          )}
          {fullTimeAddsClosed ? null : addsByCompleteTeam ? (
            <Button
              type="button"
              size="sm"
              disabled={!canEditRoster}
              onClick={() => setAddTeamOpen(true)}
              className="gap-1.5 mulish text-xs rounded-lg"
            >
              <IconUsersGroup data-icon="inline-start" />
              Add Team
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={!canEditRoster || loneFullTimeAddBlocked}
              title={
                loneFullTimeAddBlocked
                  ? (addBlockedReason ?? undefined)
                  : undefined
              }
              onClick={() => setAddOpen(true)}
              className="gap-1.5 mulish text-xs rounded-lg"
            >
              <IconPencilPlus data-icon="inline-start" />
              Add Player
            </Button>
          )}
        </div>
      </div>

      {isTeamLeague && addsByCompleteTeam && !fullTimeAddsClosed && (
        <p className="mulish m-0 flex items-start gap-1.5 text-[12.5px] text-text-muted">
          <IconInfoCircle className="mt-0.5 size-3.5 shrink-0" />
          Teams need {spellTeamSize(teamSize)} players, so add both players
          together with Add Team.
        </p>
      )}

      {!canEditRoster ? (
        <InlineHint>
          The roster is locked once the league is archived. Players can only be
          added, imported, edited, or removed while it is a draft or active.
        </InlineHint>
      ) : (
        // Not when the league grows by a whole team: the line above already
        // says so, and this would be the same rule stated twice. The Match
        // Play lock is a different message and still shows here.
        !addsByCompleteTeam &&
        !fullTimeAddsClosed &&
        addBlockedReason && <InlineHint>{addBlockedReason}</InlineHint>
      )}

      {/* The roster takes the row. The substitute pool sits beside it once the
          step is wide enough for both. */}
      <RefreshOverlay
        active={isFetching && !isLoading}
        label="Updating roster…"
      >
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <DataTable
            variant="default"
            hideColumnToggle
            fitContent
            headerRowClassName={ROSTER_HEADER_CLASS}
            rowClassName={() => ROSTER_ROW_CLASS}
            columns={columns}
            data={rosterPlayers}
            isLoading={isLoading}
            emptyState={
              <LeagueRosterEmptyState
                canManage={
                  canEditRoster && !addBlockedReason && !fullTimeAddsClosed
                }
                onAddPlayer={() => setAddOpen(true)}
                onImportPlayers={() => setImportOpen(true)}
              />
            }
            itemNoun={{ singular: "player" }}
            footerExtra={
              teamNameById
                ? ` · ${teamNameById.size} ${teamNameById.size === 1 ? "team" : "teams"}`
                : undefined
            }
          />
          <div className="w-full lg:w-85 lg:shrink-0">
            <LeagueSubstitutesRail
              substitutes={rosterSubstitutes}
              canEdit={canEditRoster}
              onEdit={setEditMember}
              onRemove={setRemoveMember}
              onAdd={() => setAddSubstituteOpen(true)}
            />
          </div>
        </div>
      </RefreshOverlay>

      <AddPlayerDialog
        // Remounted so the form resets to the role the operator asked for.
        key={addSubstituteOpen ? "add-substitute" : "add-player"}
        open={addOpen || addSubstituteOpen}
        onOpenChange={(open) => {
          if (open) return;
          setAddOpen(false);
          setAddSubstituteOpen(false);
        }}
        leagueId={leagueId}
        facilityId={facilityId}
        existingUserIds={existingUserIds}
        leagueMensTee={leagueMensTee}
        leagueWomensTee={leagueWomensTee}
        defaultSubstituteOnly={addSubstituteOpen || loneFullTimeAddBlocked}
        fullTimeAddBlockedReason={
          loneFullTimeAddBlocked ? (addBlockedReason ?? undefined) : undefined
        }
        openRoundCompletionNotice={openRoundNotice}
      />
      {addsByCompleteTeam && (
        <AddTeamDialog
          open={addTeamOpen}
          onOpenChange={setAddTeamOpen}
          leagueId={leagueId}
          facilityId={facilityId}
          existingUserIds={existingUserIds}
          teamSize={teamSize ?? undefined}
          suggestedTeamName={suggestedTeamName}
          leagueMensTee={leagueMensTee}
          leagueWomensTee={leagueWomensTee}
          openRoundCompletionNotice={openRoundNotice}
        />
      )}
      <ImportRosterDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        leagueId={leagueId}
        facilityId={facilityId}
        openRoundCompletionNotice={openRoundNotice}
      />
      {editMember && (
        <EditRosterMemberDialog
          open
          onOpenChange={(open) => !open && setEditMember(null)}
          leagueId={leagueId}
          facilityId={facilityId}
          member={editMember}
          activeLockedMessage={
            editMember.isSubstituteOnly
              ? undefined
              : rosterRemoval === "withdraw"
                ? "Use Withdraw player to leave future rounds. Completed and already-started rounds stay in the league history."
                : rosterRemoval === "none"
                  ? isTeamLeague
                    ? SINGLE_PLAYER_TEAM_WITHDRAWAL_MESSAGE
                    : MATCH_PLAY_LOCK_MESSAGE
                  : undefined
          }
        />
      )}
      <ConfirmDialog
        open={!!removeMember}
        onOpenChange={(open) => !open && setRemoveMember(null)}
        title={
          rosterRemoval === "withdraw" ? "Withdraw player?" : "Remove player?"
        }
        description={
          rosterRemoval === "withdraw"
            ? WITHDRAW_PLAYER_CONFIRMATION
            : `Remove ${removeMember?.playerName ?? "this player"} from the league roster?`
        }
        confirmLabel={rosterRemoval === "withdraw" ? "Withdraw" : "Remove"}
        onConfirm={() => void handleRemove()}
        isLoading={removing}
      />
    </div>
  );
}
